/**
 * Composition wiring for domains/google: constructs the GoogleAuthClient /
 * DriveClient / DriveLibrarySync / DriveMetadataService singletons (plus the
 * GenAI + embedding clients, quota governor, consent resolver and artifact
 * consult) with store-backed adapters and installs them into the domain
 * holders.
 *
 * Called synchronously from registerAppBootTasks() (the composition
 * manifest) — before any boot task or component can touch the holders.
 * This is where the store edges live (plan/overhaul/README.md §2 rule 3:
 * domains declare ports, app/ injects adapters):
 *
 *  - login hint   ← useSyncStore.firebaseUserEmail
 *  - connected    ← useGoogleServicesStore (a "has connected before" HINT —
 *    hint            connect/disconnect mirror it; token failures do NOT)
 *  - scan index   ← useDriveStore
 *  - imports      ← libraryController.importFile / replaceFile;
 *                   library filenames ← useBookStore.books
 */
import {
  DriveClient,
  DriveLibrarySync,
  DriveMetadataService,
  GoogleAuthClient,
  defaultPlatformOptions,
  makeLazyEmbeddingClient,
  makeLazyGenAIClient,
  setDriveClient,
  setDriveLibrarySync,
  setDriveMetadataService,
  setEmbeddingClient,
  setGenAIClient,
  setGoogleAuthClient,
  type DriveIndexEntry,
  type EmbeddingConfig,
} from '@domains/google';
import { drivePreviews } from '@data/repos/drivePreviews';
import { setConsentResolver, setQuotaScheduler } from '@kernel/net';
import { QuotaGovernor, setQuotaStore, type QuotaLimits } from '@kernel/quota';
import { makeQuotaStore } from '@app/quota/makeQuotaStore';
import { makeBackgroundQuotaLimits } from '@app/quota/embedSpendReconciler';
import { quotaCounterRepo } from '@data/repos/quotaCounter';
import { setTtsQuotaGovernor } from '@lib/tts/providers/BaseCloudProvider';
import { useGoogleServicesStore } from '@store/useGoogleServicesStore';
import { useDeviceStore } from '@store/useDeviceStore';
import { getDeviceId } from '@lib/device-id';
import { useSyncStore } from '@store/useSyncStore';
import { useDriveStore } from '@store/useDriveStore';
import { useBookStore } from '@store/useBookStore';
import { libraryController } from '@app/library/useImportController';
import { useGenAIStore, DEFAULT_QUOTA_LIMITS } from '@store/useGenAIStore';
import { usePreferencesStore } from '@store/usePreferencesStore';
import { useContentAnalysisStore } from '@store/useContentAnalysisStore';
import { makeAiConsentResolver } from './aiConsent';
import { initGenAILogPersistence } from './genaiLogPersistence';
import { makeArtifactConsult, makeArtifactConsentGate, setArtifactConsult } from './artifactConsult';
import { peekSyncOrchestrator } from '@app/sync/createSync';
import { bookContent } from '@data/repos/bookContent';
import { searchTextRepo } from '@data/repos/searchText';
import { embeddingsRepo } from '@data/repos/embeddings';
import { CURRENT_QUANT } from '@domains/search';
import { TTS_EXTRACTION_VERSION } from '@lib/ingestion/sentence-extraction';
import { createLogger } from '@lib/logger';

let wired = false;

export function wireGoogleDomain(): void {
  if (wired) return;
  wired = true;

  const auth = new GoogleAuthClient({
    platform: defaultPlatformOptions(),
    getLoginHint: () => useSyncStore.getState().firebaseUserEmail || undefined,
    hooks: {
      onConnected: (serviceId) => useGoogleServicesStore.getState().connectService(serviceId),
      onDisconnected: (serviceId) =>
        useGoogleServicesStore.getState().disconnectService(serviceId),
    },
  });
  setGoogleAuthClient(auth);

  const client = new DriveClient({ auth });
  setDriveClient(client);

  setDriveLibrarySync(
    new DriveLibrarySync({
      client,
      driveIndex: {
        getLinkedFolderId: () => useDriveStore.getState().linkedFolderId,
        getLastScanTime: () => useDriveStore.getState().lastScanTime,
        getIndex: () => useDriveStore.getState().index,
        setScanning: (isScanning) => useDriveStore.getState().setScanning(isScanning),
        setScannedFiles: (files) => useDriveStore.getState().setScannedFiles(files),
      },
      library: {
        // Drive imports flow through the SAME ImportOrchestrator queue as
        // every other entry point (ghost matching + reading-list registration
        // included). Duplicates throw DuplicateBookError — the contract the
        // Drive flows are built around.
        addBook: (file, options) =>
          options?.overwrite
            ? libraryController.replaceFile(file)
            : libraryController.importFile(file),
        getLibraryFilenames: () =>
          new Set(Object.values(useBookStore.getState().books).map((b) => b.sourceFilename)),
      },
      hasConnectedBefore: () => useGoogleServicesStore.getState().isServiceConnected('drive'),
      log: createLogger('DriveLibrarySync'),
    }),
  );

  // Partial-fetch preview service (metadata + covers via ranged reads). The
  // device-local drivePreviews repo is the cache; useDriveStore's persisted
  // index supplies {size, md5} and is READ-ONLY here (never a scan trigger).
  const toIndexEntry = (f: { id: string; size: number; md5Checksum?: string; modifiedTime: string }): DriveIndexEntry => ({
    id: f.id,
    size: f.size,
    md5Checksum: f.md5Checksum,
    modifiedTime: f.modifiedTime,
  });
  setDriveMetadataService(
    new DriveMetadataService({
      client,
      cache: {
        get: (fileId) => drivePreviews.get(fileId),
        put: (input) => drivePreviews.put(input),
        delete: (fileId) => drivePreviews.delete(fileId),
        listFileIds: () => drivePreviews.listFileIds(),
        runEviction: (valid) => drivePreviews.runEviction(valid),
      },
      index: {
        getEntry: (fileId) => {
          const f = useDriveStore.getState().index.find((e) => e.id === fileId);
          return f ? toIndexEntry(f) : undefined;
        },
        getIndex: () => useDriveStore.getState().index.map(toIndexEntry),
      },
      log: createLogger('DriveMetadataService'),
    }),
  );

  // ONE kernel quota governor, shared by the GenAI egress lane (below) and the
  // cloud-TTS lane (the audio domain holder). Limits come from the user-editable
  // GenAI settings (useGenAIStore.quotaLimits) and are read FRESH per acquire
  // inside this closure, so a settings edit takes effect on the very next
  // acquire. When pauseAllGenAI is on, limits collapse to zero so acquire throws
  // NetRateLimitedError before any network call (a master pause with no kernel
  // change). The daily request count is persisted through the injected store
  // onto the quotaCounter repo (the only IDB touch); per-minute counts live
  // in-memory in the governor.
  const getQuotaLimits = (ratePool: string): QuotaLimits => {
    const s = useGenAIStore.getState();
    if (s.pauseAllGenAI) return { rpm: 0, tpm: 0, rpd: 0, bgThrottlePercent: 0, fgRpdHeadroom: 0 };
    const base = (
      s.quotaLimitsMap[ratePool] ??
      DEFAULT_QUOTA_LIMITS[ratePool] ??
      s.quotaLimitsMap['default'] ??
      s.quotaLimits
    );
    return {
      ...base,
      bgThrottlePercent: s.bgThrottlePercent,
      fgRpdHeadroom: s.fgRpdHeadroom,
    };
  };
  // saveDailyUsage — the single chokepoint where the governor reports today's
  // usage — ALSO publishes THIS device's own spend onto its synced DeviceInfo
  // record, so every device can sum the whole project's daily quota usage.
  setQuotaStore(
    makeQuotaStore(quotaCounterRepo, (ratePool, usage) => {
      if (ratePool === 'gemini-embedding-2') {
        useDeviceStore.getState().publishEmbedSpend(getDeviceId(), usage);
      }
    }),
  );
  // The background lane's effective daily ceiling: the base daily request limit
  // reduced by the sum of what OTHER devices active today have already spent, so
  // the shared daily quota is divided across devices. Read FRESH per acquire so
  // the kernel governor is untouched. Only the background lane is routed through
  // this reduced provider; the embedding-backfill task (below) reads it as a
  // cross-device admission check before each background embed.
  const getBackgroundQuotaLimits = makeBackgroundQuotaLimits(
    () => getQuotaLimits('gemini-embedding-2'),
    () => useDeviceStore.getState().devices,
    getDeviceId(),
  );
  // The foreground/query provider (getQuotaLimits, full daily limit) and this
  // governor stay UNCHANGED, so foreground and query embeds are never divided
  // across devices.
  const governor = new QuotaGovernor(getQuotaLimits);
  // The SAME governor instance enforces admission at the NetworkGateway
  // chokepoint (acquire/release — unbypassable, like consent) AND receives the
  // post-response commit/recordCooldown from the clients.
  setQuotaScheduler(governor);
  setTtsQuotaGovernor(governor);
  // Expose the governor's live per-lane usage to the settings quota meters. The
  // governor stays the single source of truth; the store only re-exposes its
  // snapshot to the UI layer (no kernel→store edge).
  useGenAIStore.getState().setQuotaSnapshotProvider((ratePool?: string) => governor.snapshot(ratePool));
  // Install the in-memory seam the embedding-backfill task reads for its
  // cross-device admission check: this device's reduced background daily ceiling
  // plus the governor's live background daily count. Never persisted (same
  // contract as the snapshot provider), so the boot task never imports the
  // kernel governor.
  useGenAIStore
    .getState()
    .setBgBudgetProvider(getBackgroundQuotaLimits, () => governor.snapshot('gemini-embedding-2').bg.rpd);

  // Cross-restart log persistence: hydrate the store's log ring buffer from
  // the dedicated side DB, then mirror every appended entry back into it (all
  // log producers — GenAI/embedding clients and the TTS worker bridge —
  // funnel through addLog, so one store subscription covers them all).
  // Fail-soft and fire-and-forget: with IDB unavailable the buffer simply
  // stays in-memory-only.
  void initGenAILogPersistence();

  // GenAI: config read PER CALL from the store. Log entries arrive
  // pre-redacted (no inlineData bytes) and land in the store's log ring
  // buffer (persisted across restarts via the IDB mirror above; the store's
  // own partialize allowlist still excludes logs). Installed as the LAZY
  // facade — GeminiClient (and the egress plumbing behind it) loads on the
  // first generate call, keeping the GenAI implementation out of the entry
  // chunk (check 4 of scripts/check-worker-chunk.mjs).
  setGenAIClient(
    makeLazyGenAIClient({
      getConfig: () => {
        const s = useGenAIStore.getState();
        return {
          apiKey: s.apiKey,
          model: s.model,
          rotationEnabled: s.isModelRotationEnabled,
        };
      },
      onLog: (entry) => useGenAIStore.getState().addLog(entry),
      governor,
    }),
  );

  // The embedding client, installed as a LAZY facade so the real
  // GeminiEmbeddingClient (and its egress plumbing) loads only on the first
  // embed call, keeping it out of the entry chunk. Config is read PER CALL from
  // the store, so an embeddingModel/embeddingDims edit takes effect on the next
  // embed; log entries arrive pre-redacted into the same in-memory log buffer as
  // the GenAI client. The foreground-lane acquire at the gateway already
  // throttles it (no governor commit), but a 429 DOES feed the governor a
  // cooldown — the server's own retry hint, or until the next Pacific day for
  // a spent daily budget — so the indexer's next acquire is refused before the
  // network instead of collecting another 429.
  setEmbeddingClient(
    makeLazyEmbeddingClient({
      getConfig: (): EmbeddingConfig => {
        const s = useGenAIStore.getState();
        return {
          apiKey: s.apiKey,
          model: s.embeddingModel,
          dims: s.embeddingDims,
          // Opt-in batch-embedding probe flag, read per call; default-off.
          useBatchEmbedding: s.useBatchEmbedding,
        };
      },
      onLog: (entry) => useGenAIStore.getState().addLog(entry),
      governor,
    }),
  );

  // Per-book AI consent gate: the gateway consults this for non-interactive
  // 'per-book' destinations (gemini).
  setConsentResolver(
    makeAiConsentResolver({
      getConsent: (bookId) => usePreferencesStore.getState().aiConsent[bookId],
      hasAnalysisRecords: (bookId) =>
        Object.keys(useContentAnalysisStore.getState().sections).some((key) =>
          key.startsWith(`${bookId}/`),
        ),
      // The library-wide opt-in is the user's consent for bulk BACKGROUND
      // embedding: it grants egress for an unread book that has no per-book
      // consent of its own, so the background backfill can pre-embed it. Read
      // fresh so a settings flip takes effect on the next egress.
      isLibraryPreEmbedEnabled: () => useGenAIStore.getState().preEmbedLibrary,
    }),
  );

  // The read side of the shared embedding cache: before spending Gemini quota to
  // embed a book, check the user's own cloud cache and, on a hit, download and
  // reuse another device's embeddings. The store/manifest/backend edges live
  // HERE; the boot loop and reader controller inject the installed singleton's
  // port. This is read-only — uploads are handled by the publisher boot task.
  setArtifactConsult(
    makeArtifactConsult({
      // The connected cloud backend, or null when sync is off / not connected.
      // peekSyncOrchestrator never CREATES the orchestrator (no sync = null =
      // cheap no-network short-circuit). Read fresh per call.
      getBackend: () => peekSyncOrchestrator()?.getConnectedArtifactBackend() ?? null,
      getManifest: (bookId) => bookContent.getManifest(bookId),
      // The live embedding-space stamp: {model, dims} from the GenAI settings
      // (read fresh), the int8 quant literal, and the current extraction version
      // (the same one the corpus rows are stamped with), so the derived cache key
      // matches the one the publisher wrote.
      getStamp: () => {
        const s = useGenAIStore.getState();
        return {
          model: s.embeddingModel,
          dims: s.embeddingDims,
          quant: CURRENT_QUANT,
          extractionVersion: TTS_EXTRACTION_VERSION,
        };
      },
      getLiveCorpus: (bookId) => searchTextRepo.get(bookId),
      putHydrated: (row, jobRow) => embeddingsRepo.putHydrated(row, jobRow),
      // Consent gate: the per-book consent predicate (per-book consent bit OR the
      // library pre-embed opt-in for background OR the interactive reader-open
      // gesture for foreground) ANDed with the shareAiCaches master switch. With
      // sharing OFF every book is DENIED even when the user just opened it. The
      // upload (publisher) path is built from this SAME helper so the two share
      // one gating rule.
      isConsented: makeArtifactConsentGate({
        isShareEnabled: () => useGenAIStore.getState().shareAiCaches,
        isPreEmbedEnabled: () => useGenAIStore.getState().preEmbedLibrary,
        getPerBookConsent: (bookId) => usePreferencesStore.getState().aiConsent[bookId],
      }),
    }),
  );
}
